import { WebSocketState } from '../hooks/useWebSocket';

interface LiveIndicatorProps {
  state: WebSocketState;
  onReconnect?: () => void;
}

export function LiveIndicator({ state, onReconnect }: LiveIndicatorProps) {
  const getStatusInfo = () => {
    switch (state) {
      case 'connected':
        return { label: 'Live', className: 'live-connected', showReconnect: false };
      case 'connecting':
        return { label: 'Connecting...', className: 'live-connecting', showReconnect: false };
      case 'reconnecting':
        return { label: 'Reconnecting...', className: 'live-reconnecting', showReconnect: false };
      case 'disconnected':
        return { label: 'Offline', className: 'live-disconnected', showReconnect: true };
    }
  };

  const info = getStatusInfo();

  return (
    <div className={`live-indicator ${info.className}`}>
      <span className="live-dot" />
      <span className="live-label">{info.label}</span>
      {info.showReconnect && onReconnect && (
        <button className="live-reconnect" onClick={onReconnect}>
          Reconnect
        </button>
      )}
    </div>
  );
}

interface OfflineIndicatorProps {
  isOffline: boolean;
}

export function OfflineIndicator({ isOffline }: OfflineIndicatorProps) {
  if (!isOffline) return null;

  return (
    <div className="offline-banner">
      <span className="offline-icon">&#x26A0;</span>
      <span>You are offline. Some features may be unavailable.</span>
    </div>
  );
}

interface SessionWarningProps {
  expiresAt: number | null;
  warningMinutes?: number;
  onExtend?: () => void;
}

export function SessionWarning({
  expiresAt,
  warningMinutes = 5,
  onExtend,
}: SessionWarningProps) {
  if (!expiresAt) return null;

  const now = Date.now();
  const remaining = expiresAt - now;
  const warningMs = warningMinutes * 60 * 1000;

  if (remaining <= 0 || remaining > warningMs) return null;

  const minutesLeft = Math.ceil(remaining / 60000);

  return (
    <div className="session-warning">
      <span>Your session expires in {minutesLeft} minute{minutesLeft !== 1 ? 's' : ''}.</span>
      {onExtend && (
        <button className="session-extend" onClick={onExtend}>
          Extend Session
        </button>
      )}
    </div>
  );
}

interface QuotaMeterProps {
  label: string;
  used: number;
  limit: number;
  unit?: string;
}

export function QuotaMeter({ label, used, limit, unit = '' }: QuotaMeterProps) {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isWarning = percentage >= 70;
  const isCritical = percentage >= 90;

  return (
    <div className="quota-meter">
      <div className="quota-header">
        <span className="quota-label">{label}</span>
        <span className="quota-value">
          {used.toLocaleString()} / {limit.toLocaleString()} {unit}
        </span>
      </div>
      <div className="quota-bar-container">
        <div
          className={`quota-bar ${isCritical ? 'critical' : isWarning ? 'warning' : ''}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
